import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import { type Task, task } from 'ember-concurrency';
import type MarketSnapshotsOverviewController from 'lumen/controllers/market/snapshots/overview';
import type MarketPerformanceDataItem from 'lumen/models/market-performance/data-item';
import {
  type Kpi,
  KPIS,
  type MarketPerformanceMeta,
} from 'lumen/models/market-performance/data-item';
import type { HotelAndMarketServiceWithMarket } from 'lumen/services/hotel-and-market';
import { LOCAL_STORAGE_KEYS } from 'lumen/services/local-storage';
import type { Transition } from 'lumen/services/transition';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import {
  type DataPreferences,
  type ForecastColumn,
  getOverviewColumnMetrics,
  GROUP_BY,
  GROUP_COLUMNS_BY,
  type GroupBy,
  OVERVIEW_COLUMNS,
  type OverviewColumn,
  parseDataPreferences,
  parseTableLayoutPreferences,
  type SdlyColumn,
  type TableLayoutPreferences,
} from 'lumen/utils/constants/market-snapshots-constants';
import protectedRoute from 'lumen/utils/decorators/protected-route';
import {
  type ColumnSettings,
  defaultColumnSettings,
  getVisibleKpis,
  getVisibleMetricTypes,
  parseColumnSettings,
} from 'lumen/utils/market-snapshots-column-settings';
import {
  buildApiOrderBy,
  isSortedColumnHidden,
} from 'lumen/utils/market-snapshots-order-by';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

export type MarketSnapshotsOverviewParams = {
  fromDate?: string;
  groupBy?: GroupBy;
  orderBy?: string;
  page?: number;
  pageSize?: number;
  toDate?: string;
};

export type LoadDataItemsParams = {
  forecastColumn: ForecastColumn;
  fromDate: string;
  groupBy: GroupBy;
  marketId: HotelAndMarketServiceWithMarket['market']['id'];
  orderBy: string;
  page: number;
  pageSize: number;
  sdlyColumn: SdlyColumn;
  toDate: string;
};

export type LoadedDataItems = {
  items: MarketPerformanceDataItem[];
  meta: MarketPerformanceMeta;
  // The params this result was loaded with, so consumers can tell whether it
  // still matches the current query params.
  params: LoadDataItemsParams;
};

type Model = {
  loadDataItemsTask: Task<LoadedDataItems, [LoadDataItemsParams]>;
};

@protectedRoute({
  features: ['reservations.market.overview'],
})
export default class MarketSnapshotsOverviewRoute extends Route {
  queryParams = {
    fromDate: { refreshModel: true },
    toDate: { refreshModel: true },
    groupBy: { refreshModel: true },
    orderBy: { refreshModel: true },
    page: { refreshModel: true },
    pageSize: { refreshModel: true },
  };

  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithMarket;
  @service('local-storage') declare localStorage: Services['local-storage'];
  @service declare store: Services['store'];
  @service declare transition: Services['transition'];

  loadDataItemsTask = task(
    { restartable: true },
    async (params: LoadDataItemsParams): Promise<LoadedDataItems> => {
      const {
        fromDate,
        toDate,
        groupBy,
        marketId,
        orderBy,
        page,
        pageSize,
        sdlyColumn,
        forecastColumn,
      } = params;

      try {
        const response = await this.store.query<MarketPerformanceDataItem>(
          'market-performance/data-item',
          {
            namespace: APIGATEWAY,
            typeKey: `reservations/market/${marketId}/performance`,
            view: 'overview',
            compare: `${sdlyColumn},ly,${forecastColumn},budget`,
            group_by: groupBy,
            start_date: fromDate,
            end_date: toDate,
            market_id: marketId,
            // Month rows are few and API-ordered: no sort, no pagination.
            ...(groupBy === GROUP_BY.PROPERTY && {
              order_by: orderBy,
              page,
              page_size: pageSize,
            }),
          },
        );

        return {
          items: response,
          meta: response.meta as unknown as MarketPerformanceMeta,
          params,
        };
      } catch (error) {
        void this.alerting.showErrorAlert();
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  get storedColumnSettings(): ColumnSettings<OverviewColumn> {
    return this.readColumnSettings(
      LOCAL_STORAGE_KEYS.MARKET_SNAPSHOTS_OVERVIEW_COLUMNS,
      OVERVIEW_COLUMNS,
    );
  }

  get storedDataPreferences(): DataPreferences {
    const stored = this.localStorage.getItem(
      LOCAL_STORAGE_KEYS.MARKET_SNAPSHOTS_DATA_PREFERENCES,
    );

    if (!stored) {
      return parseDataPreferences(null);
    }

    try {
      return parseDataPreferences(JSON.parse(stored));
    } catch {
      return parseDataPreferences(null);
    }
  }

  get storedKpiColumnSettings(): ColumnSettings<Kpi> {
    return this.readColumnSettings(
      LOCAL_STORAGE_KEYS.MARKET_SNAPSHOTS_OVERVIEW_KPI_COLUMNS,
      KPIS,
      true,
    );
  }

  get storedTableLayoutPreferences(): TableLayoutPreferences {
    const stored = this.localStorage.getItem(
      LOCAL_STORAGE_KEYS.MARKET_SNAPSHOTS_TABLE_LAYOUT,
    );

    if (!stored) {
      return parseTableLayoutPreferences(null);
    }

    try {
      return parseTableLayoutPreferences(JSON.parse(stored));
    } catch {
      return parseTableLayoutPreferences(null);
    }
  }

  beforeModel(transition: Transition): void {
    this.transition.validateMarketSnapshotsQueryParams(
      transition,
      this.paramsFor(this.routeName),
    );
  }

  /** A bookmarked or stale sort on a column the stored layout hides falls back to `name`. */
  private effectiveOrderBy(orderBy: string): string {
    const layout = this.storedTableLayoutPreferences;
    const isHidden = isSortedColumnHidden(orderBy, {
      isKpiMode: layout.groupColumnsBy === GROUP_COLUMNS_BY.KPIS,
      visibleKpis: getVisibleKpis(this.storedKpiColumnSettings),
      visibleMetricTypes: getVisibleMetricTypes(
        this.storedColumnSettings,
        layout,
        getOverviewColumnMetrics(this.storedDataPreferences),
      ),
    });

    return isHidden ? 'name' : orderBy;
  }

  model(params: MarketSnapshotsOverviewParams): Model {
    const today = this.hotelAndMarketService.getLocalTime();

    const fromDate =
      params.fromDate ?? today.startOf('month').format('YYYY-MM-DD');
    const toDate = params.toDate ?? today.endOf('month').format('YYYY-MM-DD');

    const { forecastColumn, sdlyColumn, variance } = this.storedDataPreferences;
    const { sortOn } = this.storedTableLayoutPreferences;

    void this.loadDataItemsTask.perform({
      fromDate,
      toDate,
      groupBy: params.groupBy ?? GROUP_BY.PROPERTY,
      marketId: this.hotelAndMarketService.market.id,
      // The URL keeps the semantic sort; the API form is derived only here.
      orderBy: buildApiOrderBy(
        this.effectiveOrderBy(params.orderBy ?? 'name'),
        sortOn,
        variance,
      ),
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 25,
      sdlyColumn,
      forecastColumn,
    });

    return {
      loadDataItemsTask: this.loadDataItemsTask,
    };
  }

  private readColumnSettings<T extends string>(
    key: (typeof LOCAL_STORAGE_KEYS)[keyof typeof LOCAL_STORAGE_KEYS],
    ids: readonly T[],
    requireVisible = false,
  ): ColumnSettings<T> {
    const stored = this.localStorage.getItem(key);

    try {
      return parseColumnSettings(
        JSON.parse(stored ?? 'null'),
        ids,
        requireVisible,
      );
    } catch {
      return defaultColumnSettings(ids);
    }
  }

  setupController(
    controller: MarketSnapshotsOverviewController,
    model: Model,
  ): void {
    super.setupController(controller, model);

    controller.columnSettings = this.storedColumnSettings;
    controller.dataPreferences = this.storedDataPreferences;
    controller.kpiColumnSettings = this.storedKpiColumnSettings;
    controller.tableLayoutPreferences = this.storedTableLayoutPreferences;
    // Keep the URL QP in line with what model() fetched.
    const orderBy = this.effectiveOrderBy(controller.orderBy);

    if (orderBy !== controller.orderBy) {
      controller.orderBy = orderBy;
    }
  }
}
